/**
 * Shared palette helpers — HSL conversion + hill-layer palette derivation.
 *
 * Used by both the in-game parallax background and the title-screen
 * backdrop so the two share a single visual language: pick a hue, derive
 * three hills at progressively lighter/less-saturated tints (fog/haze).
 */

import type { Color } from './index';

/** HSL → Color. h, s, l ∈ [0, 1]. */
export function hslToColor(h: number, s: number, l: number): Color {
  const hue = ((h % 1) + 1) % 1;
  if (s === 0) return { r: l, g: l, b: l, a: 1 };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hueToChannel(p, q, hue + 1 / 3),
    g: hueToChannel(p, q, hue),
    b: hueToChannel(p, q, hue - 1 / 3),
    a: 1,
  };
}

function hueToChannel(p: number, q: number, t: number): number {
  let x = t;
  if (x < 0) x += 1;
  if (x > 1) x -= 1;
  if (x < 1 / 6) return p + (q - p) * 6 * x;
  if (x < 1 / 2) return q;
  if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
  return p;
}

/** Extract the hue component (0..1) of an RGB color. */
export function rgbToHue(c: Color): number {
  const max = Math.max(c.r, c.g, c.b);
  const min = Math.min(c.r, c.g, c.b);
  const d = max - min;
  if (d < 1e-6) return 0;
  let h: number;
  if (max === c.r)      h = ((c.g - c.b) / d) % 6;
  else if (max === c.g) h = (c.b - c.r) / d + 2;
  else                  h = (c.r - c.g) / d + 4;
  h /= 6;
  if (h < 0) h += 1;
  return h;
}

/**
 * Three parallax-hill colors derived from a base hue.
 * Far → near: progressively deeper saturation, darker lightness — the
 * farthest hill is pale and hazy, the nearest is bold silhouette.
 */
export function deriveHillPalette(hue: number): [Color, Color, Color] {
  return [
    hslToColor(hue, 0.25, 0.55),  // farthest — palest, hazy
    hslToColor(hue, 0.35, 0.42),
    hslToColor(hue, 0.45, 0.30),  // nearest — deepest
  ];
}
